/**
 * Client-side helper for Vertex AI Search API.
 * Routes natural-language queries to /api/search and converts
 * results to the same SearchResult shape used by MiniSearch.
 */

import type { SearchResult } from './search-client';
import { PAGE_TYPE_ICONS } from './search-categories';

// ---------------------------------------------------------------------------
// Query classifier: detect natural-language queries
// ---------------------------------------------------------------------------
const NL_KEYWORDS = /\b(when|what|how|where|which|why|best|is there|are there|can i|should i|tell me|find me)\b/i;
// Indonesian NL keywords: kapan (when), apa (what), bagaimana (how), di mana/dimana (where), mengapa/kenapa (why), yang mana (which)
const NL_KEYWORDS_ID = /\b(kapan|apa|bagaimana|di\s?mana|dimana|mengapa|kenapa|yang mana|apakah|berapa|terbaik)\b/i;
// Arabic NL keywords: متى (when), ما (what), كيف (how), أين (where), لماذا (why), أي (which)
const NL_KEYWORDS_AR = /(?:متى|ما هو|ما هي|كيف|أين|لماذا|أي|هل|كم|أفضل)/;
const HAS_QUESTION_MARK = /\?/;

export function isNaturalLanguageQuery(query: string): boolean {
  if (!query || query.length < 4) return false;
  const wordCount = query.trim().split(/\s+/).length;
  if (wordCount > 4) return true;
  if (HAS_QUESTION_MARK.test(query)) return true;
  if (NL_KEYWORDS.test(query)) return true;
  if (NL_KEYWORDS_ID.test(query)) return true;
  if (NL_KEYWORDS_AR.test(query)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Vertex AI Search response shape (from /api/search)
// ---------------------------------------------------------------------------
interface VertexSearchResponse {
  results: Array<{
    title: string;
    url: string;
    snippet: string;
    pageType: string;
    countryCode: string;
  }>;
  totalResults: number;
  correctedQuery?: string;
}

function countryCodeToFlag(code: string): string {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return '';
  const offset = 0x1F1E6 - 65;
  return String.fromCodePoint(upper.charCodeAt(0) + offset) +
    String.fromCodePoint(upper.charCodeAt(1) + offset);
}

// ---------------------------------------------------------------------------
// Fetch Vertex AI Search results
// ---------------------------------------------------------------------------
export async function vertexSearch(
  query: string,
  options?: { limit?: number; signal?: AbortSignal }
): Promise<{ results: SearchResult[]; correctedQuery?: string }> {
  const resp = await fetch('/api/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, limit: options?.limit || 10 }),
    signal: options?.signal,
  });

  if (!resp.ok) {
    throw new Error(`Vertex search failed: ${resp.status}`);
  }

  const data: VertexSearchResponse = await resp.json();

  const results: SearchResult[] = data.results.map((r, i) => {
    // Validate URL is a safe relative path (defense against datastore poisoning)
    const href = r.url && r.url.startsWith('/') && !r.url.startsWith('//') ? r.url : '';
    if (!href) return null;
    return {
    id: href,
    title: r.title,
    description: r.snippet,
    keywords: '',
    href,
    pageType: r.pageType,
    icon: PAGE_TYPE_ICONS[r.pageType] || '\u{1F4C4}',
    countryCode: r.countryCode,
    countryFlag: countryCodeToFlag(r.countryCode),
    sublabel: r.snippet.substring(0, 80),
    score: 100 - i, // synthetic score preserving Vertex ranking (outranks MiniSearch when mixed)
  };
  }).filter((r): r is SearchResult => r !== null);

  return { results, correctedQuery: data.correctedQuery };
}
